/**
 * ============================================================================
 * PRODUCT PICKER v1.0
 * ============================================================================
 *
 * Modal zur Artikelauswahl aus vordefiniertem Produktkatalog.
 * User wählt Produkte aus statt sie manuell einzugeben.
 *
 * Features:
 * - Kategorien mit einklappbaren Produktlisten
 * - Suchfunktion
 * - Häufig gekaufte Produkte
 * - Inline Mengenauswahl
 * - Warenkorb-Vorschau
 *
 * @version 1.0.0
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  PRODUCT_CATALOG,
  ALL_PRODUCTS,
  searchProducts,
  getFrequentProducts,
  UNITS
} from './productCatalog';
import './ProductPicker.css';

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

// Suchleiste
const ProductSearch = ({ value, onChange, onClear }) => {
  const inputRef = useRef(null);

  useEffect(() => {
    // Auto-focus on mount
    inputRef.current?.focus();
  }, []);

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      background: 'white',
      border: '1.5px solid #B8D4DC',
      borderRadius: '8px',
      padding: '0 10px'
    }}>
      <span style={{ fontSize: '0.9rem', marginRight: '8px', opacity: 0.6 }}>🔍</span>
      <input
        ref={inputRef}
        type="text"
        placeholder="Artikel suchen..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          flex: 1,
          border: 'none',
          background: 'transparent',
          padding: '8px 0',
          fontSize: '0.85rem',
          outline: 'none'
        }}
      />
      {value && (
        <button
          onClick={onClear}
          style={{
            background: '#E5E7EB',
            border: 'none',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            fontSize: '0.7rem',
            cursor: 'pointer',
            color: '#6B7280'
          }}
        >
          ✕
        </button>
      )}
    </div>
  );
};

// Einzelnes Produkt in der Liste
const ProductItem = ({ product, isSelected, selectedQty, selectedUnit, onToggle, onQtyChange, onUnitChange }) => {
  const handleClick = () => {
    if (!isSelected) {
      onToggle(product, product.defaultQty, product.defaultUnit);
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    onToggle(product, null, null);
  };

  const adjustQty = (delta, e) => {
    e.stopPropagation();
    const newQty = Math.max(1, (selectedQty || product.defaultQty) + delta);
    onQtyChange(product.id, newQty);
  };

  return (
    <div
      className={`product-item ${isSelected ? 'selected' : ''}`}
      onClick={handleClick}
    >
      <span className="product-emoji">{product.emoji}</span>
      <span className="product-name">{product.name}</span>

      {isSelected ? (
        <div className="product-controls" onClick={(e) => e.stopPropagation()}>
          <button className="qty-btn" onClick={(e) => adjustQty(-getQtyStep(selectedUnit), e)}>−</button>
          <input
            type="number"
            value={selectedQty}
            onChange={(e) => onQtyChange(product.id, parseFloat(e.target.value) || 1)}
            min="1"
            style={{
              width: '60px',
              textAlign: 'center',
              border: '1.5px solid #B8D4DC',
              borderRadius: '4px',
              padding: '2px 4px',
              fontSize: '0.8rem',
              fontWeight: '600',
              height: '24px',
              boxSizing: 'border-box',
              background: 'white'
            }}
          />
          <select
            className="unit-select"
            value={selectedUnit}
            onChange={(e) => onUnitChange(product.id, e.target.value)}
          >
            {UNITS.map(unit => (
              <option key={unit} value={unit}>{unit}</option>
            ))}
          </select>
          <button className="qty-btn" onClick={(e) => adjustQty(getQtyStep(selectedUnit), e)}>+</button>
          <button className="remove-btn" onClick={handleRemove}>✕</button>
        </div>
      ) : (
        <button className="add-btn">+</button>
      )}
    </div>
  );
};

// Einklappbare Kategorie
const ProductCategory = ({
  category,
  isExpanded,
  onToggleExpand,
  selectedItems,
  onToggleProduct,
  onQtyChange,
  onUnitChange
}) => {
  const selectedCount = category.products.filter(p =>
    selectedItems.some(s => s.id === p.id)
  ).length;

  return (
    <div className="product-category">
      <div
        className={`category-header ${isExpanded ? 'expanded' : ''}`}
        onClick={onToggleExpand}
      >
        <span className="category-emoji">{category.emoji}</span>
        <span className="category-label">{category.label}</span>
        {selectedCount > 0 && (
          <span className="category-badge">{selectedCount}</span>
        )}
        <span className="category-chevron">{isExpanded ? '▼' : '▶'}</span>
      </div>

      {isExpanded && (
        <div className="category-products">
          {category.products.map(product => {
            const selected = selectedItems.find(s => s.id === product.id);
            return (
              <ProductItem
                key={product.id}
                product={product}
                isSelected={!!selected}
                selectedQty={selected?.quantity}
                selectedUnit={selected?.unit}
                onToggle={onToggleProduct}
                onQtyChange={onQtyChange}
                onUnitChange={onUnitChange}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

// Warenkorb-Vorschau
const ProductCart = ({ items, onRemove, onClear }) => {
  if (items.length === 0) return null;

  return (
    <div className="product-cart">
      <div className="cart-header">
        <span className="cart-icon">🛒</span>
        <span className="cart-count">{items.length} Artikel</span>
        {items.length > 1 && (
          <button className="cart-clear" onClick={onClear}>
            Alle entfernen
          </button>
        )}
      </div>
      <div className="cart-items">
        {items.map(item => (
          <div key={item.id} className="cart-item">
            <span className="cart-item-emoji">{item.emoji}</span>
            <span className="cart-item-name">{item.name}</span>
            <span className="cart-item-qty">{item.quantity} {item.unit}</span>
            <button className="cart-item-remove" onClick={() => onRemove(item.id)}>
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper: Mengen-Schritte basierend auf Einheit
const getQtyStep = (unit) => {
  switch (unit) {
    case 'g': return 50;
    case 'kg': return 0.5;
    case 'ml': return 50;
    case 'L': return 0.5;
    default: return 1;
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ProductPicker = ({ isOpen, onClose, onAddItems, existingItems = [] }) => {
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState(['obst-gemuese', 'milchprodukte']);

  // Memoized Data
  const frequentProducts = useMemo(() => getFrequentProducts(), []);

  const searchResults = useMemo(() => {
    if (searchQuery.length < 2) return [];
    return searchProducts(searchQuery);
  }, [searchQuery]);

  const isSearching = searchQuery.length >= 2;

  // Handlers
  const toggleCategory = useCallback((categoryId) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  }, []);

  const toggleProduct = useCallback((product, qty, unit) => {
    const exists = selectedItems.find(p => p.id === product.id);

    if (exists) {
      // Deselect — nur visuell entfernen (bereits gespeichert)
      setSelectedItems(prev => prev.filter(p => p.id !== product.id));
    } else {
      // Select + sofort zur Liste hinzufügen
      const newItem = {
        id: 'item-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
        name: (product.emoji || '') + ' ' + product.name,
        quantity: qty || product.defaultQty,
        unit: unit || product.defaultUnit,
        category: product.categoryLabel || getCategoryLabel(product.id),
        checked: false,
      };
      onAddItems([newItem]);
      setSelectedItems(prev => [...prev, {
        id: product.id,
        name: product.name,
        emoji: product.emoji,
        quantity: qty || product.defaultQty,
        unit: unit || product.defaultUnit,
        category: product.categoryLabel || getCategoryLabel(product.id),
      }]);
    }
  }, [selectedItems, onAddItems]);

  const updateQuantity = useCallback((productId, newQty) => {
    setSelectedItems(prev =>
      prev.map(item =>
        item.id === productId ? { ...item, quantity: newQty } : item
      )
    );
  }, []);

  const updateUnit = useCallback((productId, newUnit) => {
    setSelectedItems(prev =>
      prev.map(item =>
        item.id === productId ? { ...item, unit: newUnit } : item
      )
    );
  }, []);

  const removeFromCart = useCallback((productId) => {
    setSelectedItems(prev => prev.filter(p => p.id !== productId));
  }, []);

  const clearCart = useCallback(() => {
    setSelectedItems([]);
  }, []);

  const handleAddToList = () => {
    if (selectedItems.length === 0) return;

    // Format items for ListDetail
    const newItems = selectedItems.map(item => ({
      id: 'item-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
      name: item.emoji + ' ' + item.name,
      quantity: item.quantity,
      unit: item.unit,
      category: item.category,
      checked: false,
    }));

    onAddItems(newItems);
    setSelectedItems([]);
    setSearchQuery('');
    onClose();
  };

  const handleClose = () => {
    setSelectedItems([]);
    setSearchQuery('');
    onClose();
  };

  // handleAddToList nicht mehr nötig — Artikel werden sofort bei Auswahl gespeichert

  // Helper: Get category label for a product
  const getCategoryLabel = (productId) => {
    for (const cat of Object.values(PRODUCT_CATALOG)) {
      if (cat.products.some(p => p.id === productId)) {
        return cat.label;
      }
    }
    return 'Sonstiges';
  };

  // Don't render if not open
  if (!isOpen) return null;

  return (
    <div className="product-picker-overlay" onClick={handleClose}>
      <div className="product-picker-modal" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="picker-header">
          <h2 className="picker-title">
            <span>📦</span> Artikel auswählen
          </h2>
          <button className="picker-close" onClick={handleClose}>✕</button>
        </div>

        {/* Search */}
        <div className="picker-search">
          <ProductSearch
            value={searchQuery}
            onChange={setSearchQuery}
            onClear={() => setSearchQuery('')}
          />
        </div>

        {/* Content */}
        <div className="picker-content">

          {isSearching ? (
            // Search Results
            <div className="search-results">
              <div className="section-header">
                🔍 Suchergebnisse ({searchResults.length})
              </div>
              {searchResults.length === 0 ? (
                <div className="no-results">
                  <p>Keine Produkte gefunden für "{searchQuery}"</p>
                  <p className="hint">Tipp: Du kannst auch eigene Artikel hinzufügen</p>
                </div>
              ) : (
                <div className="results-list">
                  {searchResults.map(product => {
                    const selected = selectedItems.find(s => s.id === product.id);
                    return (
                      <ProductItem
                        key={product.id}
                        product={product}
                        isSelected={!!selected}
                        selectedQty={selected?.quantity}
                        selectedUnit={selected?.unit}
                        onToggle={toggleProduct}
                        onQtyChange={updateQuantity}
                        onUnitChange={updateUnit}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            // Category Browsing
            <>
              {/* Frequent Products */}
              <div className="frequent-section">
                <div className="section-header">
                  ⭐ Häufig gekauft
                </div>
                <div className="frequent-chips">
                  {frequentProducts.map(product => {
                    const isSelected = selectedItems.some(s => s.id === product.id);
                    return (
                      <button
                        key={product.id}
                        className={`frequent-chip ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleProduct(product, product.defaultQty, product.defaultUnit)}
                      >
                        <span>{product.emoji}</span>
                        <span>{product.name}</span>
                        {isSelected && <span className="chip-check">✓</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Categories */}
              <div className="categories-section">
                {Object.values(PRODUCT_CATALOG).map(category => (
                  <ProductCategory
                    key={category.id}
                    category={category}
                    isExpanded={expandedCategories.includes(category.id)}
                    onToggleExpand={() => toggleCategory(category.id)}
                    selectedItems={selectedItems}
                    onToggleProduct={toggleProduct}
                    onQtyChange={updateQuantity}
                    onUnitChange={updateUnit}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="picker-footer">
          <button className="btn-add" onClick={handleClose} style={{ flex: 1 }}>
            ✓ Fertig {selectedItems.length > 0 ? `(${selectedItems.length} hinzugefügt)` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductPicker;
